/**
 * useMessaging — message-library data hook (Greg's lifecycle shape, 2026-07-07)
 * re-grafted onto the live CRUD wiring (#728, 2026-07-07 handback).
 *
 * Reads through the typed client (`listBuilderMessages`); server rows are
 * overlaid onto any locally-known content fields. The demo seed is EMPTY per
 * the seed-guard — rich content fields (subject/perChannel/audience/whatsapp)
 * must come from the mock layer (REQ-017).
 *
 * Mutations call the live createMessage/updateMessage/deleteMessage/
 * duplicateMessage ops, AWAITED, before the cache updates (no-fake-
 * interactivity mandate). Callers surface failures as real error states.
 */
import { t as i18nT } from "../../lib/i18n";
import { useEffect, useState } from "react";
import { listBuilderMessages, apiRaw } from "@v0y4ge/api-client";
import type { MessageItem } from "./messaging-seed";
import { logUpsert, logDelete } from "../../lib/audit";
import { onTenantChange, onTenantSwitching } from "../../shell/access";

let _cache: MessageItem[] | null = null;
/** Non-null when the last read FAILED (as opposed to returning nothing). The
 *  distinction is the whole point: an empty library and a broken read looked
 *  identical twice in one day, and the second time it read as data loss. */
let _loadError: string | null = null;
const _listeners = new Set<() => void>();
const notify = () => _listeners.forEach((fn) => fn());
onTenantSwitching(() => { _cache = null; notify(); });
onTenantChange(() => { void loadAll(); });

let _seq = 0;
const tmpId = () => `m_new_${Date.now().toString(36)}${(_seq++).toString(36)}`;

/** The real message-write contract's `channels` enum only knows
 *  email|sms|whatsapp|push (confirmed live, 2026-07-31 — a write carrying our
 *  UI-only "calendar"/"wallet" pseudo-channels came back as a Zod
 *  invalid_enum_value rejection AFTER a successful 200, which read as "can't
 *  unlock the message"). Calendar/wallet are real in the UI (their own tab,
 *  their own content) but are never valid members of the wire `channels`
 *  array — their presence is carried by `ics`/`wallet` fields instead. Strip
 *  them from the OUTGOING payload only; the local cache keeps the full set so
 *  the channel picker doesn't lose the selection. */
const WIRE_CHANNELS = new Set(["email", "sms", "whatsapp", "push"]);

/* Two-status lifecycle (operator, 2026-08-04): a message is DRAFT or LIVE, nothing else.
   Legacy stored values ("approved", "scheduled", "sent", "awaiting_approval") all read as
   live — normalizing here, at the one read seam, retires them from every screen at once. */
const normStatus = (s: unknown): "draft" | "live" => (s === "draft" ? "draft" : "live");

/* The wire carries only real delivery channels (contract conformance, 2026-08-04); the UI's
   wallet/calendar pseudo-channels are re-derived here from the fields that actually hold their
   content, so chips and composer tabs light up without dirtying the wire enum. */
/* Engine writes stamp ISO timestamps into updatedAt; the column is display text, so ISO strings
   are humanized here at the read seam. Non-ISO values ("Today", "2d ago") pass through. */
const humanWhen = (v: unknown): string => {
  const s = String(v ?? "—");
  if (!/^\d{4}-\d{2}-\d{2}T/.test(s)) return s;
  const d = new Date(s);
  if (isNaN(d.getTime())) return s;
  const mins = Math.max(0, Math.round((Date.now() - d.getTime()) / 60000));
  if (mins < 1) return "Just now";
  if (mins < 60) return mins + "m ago";
  if (mins < 60 * 24) return Math.round(mins / 60) + "h ago";
  if (mins < 60 * 24 * 7) return Math.round(mins / (60 * 24)) + "d ago";
  return d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
};

const withPseudo = (ch: string[], s: Record<string, any>): string[] => {
  const out = (ch || []).slice();
  if (s.wallet && out.indexOf("wallet") < 0) out.push("wallet");
  if (s.ics && out.indexOf("calendar") < 0) out.push("calendar");
  return out;
};
const wireSafe = (m: MessageItem): MessageItem => ({ ...m, channels: (m.channels || []).filter((c) => WIRE_CHANNELS.has(c)) as typeof m.channels });

/** Direct REST fallback for the four message-write ops. The typed
 *  createMessage/updateMessage/deleteMessage/duplicateMessage names are not
 *  exported by the real `@v0y4ge/api-client` (only the retired, unused stub had
 *  them) — `_op()` saw `undefined` and silently skipped the network call
 *  entirely, so a write only ever touched the in-memory cache. These call the
 *  same REST paths `wf-preview-seeds.js` already serves, exactly like the read
 *  path's `apiRaw` fallback in `loadAll` below. */
const _restCreate = (m: MessageItem) => apiRaw<Record<string, any>>("/v1/admin/messages", { method: "POST", body: JSON.stringify(wireSafe(m)) }, '_restCreate');
const _restUpdate = (id: string, m: MessageItem) => apiRaw<Record<string, any>>(`/v1/admin/messages/${encodeURIComponent(id)}`, { method: "PUT", body: JSON.stringify(wireSafe(m)) }, '_restUpdate');
const _restDelete = (id: string) => apiRaw<Record<string, any>>(`/v1/admin/messages/${encodeURIComponent(id)}`, { method: "DELETE" }, '_restDelete');
const _restDuplicate = (id: string) => apiRaw<Record<string, any>>(`/v1/admin/messages/${encodeURIComponent(id)}/duplicate`, { method: "POST" }, '_restDuplicate');

function fromServer(s: Record<string, any>): MessageItem {
  return {
    id: s.id, name: s.name ?? "", channels: withPseudo(s.channels ?? ["email"], s),
    status: normStatus(s.status ?? "draft"), audience: s.audience ?? "All",
    updated: humanWhen(s.updatedAt ?? s.updated ?? "—"), subject: s.subject ?? "",
    perChannel: s.perChannel ?? {}, body: s.body ?? undefined,
    whatsapp: s.whatsapp ?? undefined, quickReplies: s.quickReplies ?? undefined,
    // REQ-112: the multilingual + email-design fields. These were dropped here,
    // so a server row carrying an emailDesign arrived with none and every seeded
    // email fell back to plain text — which read as "the logo isn't displaying"
    // (operator, 2026-07-31). Anything the server sends now survives the mapping.
    languages: s.languages ?? undefined,
    defaultLanguage: s.defaultLanguage ?? undefined,
    translations: s.translations ?? undefined,
    emailDesign: s.emailDesign ?? undefined,
    emailHtml: s.emailHtml ?? undefined,
    ics: s.ics ?? undefined,
    wallet: s.wallet ?? undefined,
  };
}

function overlaySeed(server: Record<string, any>[]): MessageItem[] {
  // Seed is empty under the seed-guard; keep the overlay so any locally-cached
  // content survives a refetch, and msg_* referenced ids resolve when the mock
  // layer serves them (REQ-017).
  const byId = new Map<string, MessageItem>([].map((m: MessageItem) => [m.id, { ...m }]));
  const extra: MessageItem[] = [];
  server.forEach((s: Record<string, any>) => {
    const id = s.id;
    const existing = id ? byId.get(id) : undefined;
    if (existing) {
      existing.name = s.name ?? existing.name;
      existing.channels = withPseudo(s.channels ?? existing.channels, s);
      existing.status = normStatus(s.status ?? existing.status);
      existing.audience = s.audience ?? existing.audience;
      existing.updated = humanWhen(s.updatedAt ?? s.updated ?? existing.updated);
      // Same fields as fromServer — a refetch must not strip a design or a
      // language set off a row the cache already knows about.
      if (s.perChannel) existing.perChannel = s.perChannel;
      if (s.subject != null) existing.subject = s.subject;
      if (s.languages) existing.languages = s.languages;
      if (s.defaultLanguage) existing.defaultLanguage = s.defaultLanguage;
      if (s.translations) existing.translations = s.translations;
      if (s.emailDesign) existing.emailDesign = s.emailDesign;
      if (s.emailHtml) existing.emailHtml = s.emailHtml;
      if (s.ics) existing.ics = s.ics;
      if (s.wallet) existing.wallet = s.wallet;
    } else if (id) {
      extra.push(fromServer(s));
    }
  });
  return [...extra, ...byId.values()];
}

async function loadAll() {
  /* A failed read must not look like "you have no messages" (2026-07-31). The
     typed op's path has moved between kit versions; when it fails we retry the
     canonical library path directly through the same client choke point, and only
     then record a real error. */
  let arr: Record<string, any>[] = [];
  let failed: unknown = null;
  const r = await listBuilderMessages().catch((e) => { failed = e; console.warn("listBuilderMessages failed:", e); return null; });
  arr = r ? ((r as any).messages ?? (r as any).data ?? []) : [];
  if (!arr.length) {
    try {
      const alt = await apiRaw<{ messages?: Record<string, any>[]; data?: Record<string, any>[] }>("/v1/admin/messages");
      arr = alt?.messages ?? alt?.data ?? [];
      if (arr.length) { failed = null; console.warn("listBuilderMessages returned nothing; served the library from /v1/admin/messages instead."); }
    } catch (e) {
      if (failed) console.warn("message library unavailable from both paths:", e);
      else failed = arr.length ? null : failed;
    }
  }
  _loadError = arr.length ? null : (failed ? String((failed as { message?: string }).message || failed) : null);
  _cache = overlaySeed(arr);
  notify();
}

export function useMessaging() {
  const [, setV] = useState(0);
  useEffect(() => {
    const fn = () => setV((v) => v + 1);
    _listeners.add(fn);
    if (!_cache) loadAll();
    return () => { _listeners.delete(fn); };
  }, []);

  return {
    all: (): MessageItem[] => _cache ?? [],
    loading: _cache === null,
    /** Set when the last read failed. Screens MUST distinguish this from an empty
     *  library — rendering "no messages yet" over a failed read is how two
     *  separate faults hid in plain sight on 2026-07-31. */
    error: _loadError,
    get: (id: string) => (_cache ?? []).find((m) => m.id === id) ?? null,
    newMessage: (): MessageItem => ({ id: tmpId(), name: "", channels: ["email"], status: "draft", audience: "All", updated: "Just now", subject: "", perChannel: {}, body: "" }), // ui-local — unsaved draft shape
    /** Awaited client write first; cache+notify only on success (#728). */
    upsert: async (m: MessageItem): Promise<MessageItem> => {
      const idx = (_cache ?? []).findIndex((x) => x.id === m.id);
      const isNew = idx < 0 || m.id.startsWith("m_new_");
      const saved: MessageItem = { ...m, updated: "Just now" };
      // ALWAYS the direct REST call (2026-08-04). The typed createMessage/updateMessage names
      // are not exported by the real client, so the import resolves to a barrel-patch SHIM that
      // returns {} without touching the network — a "successful" write that never reached the
      // server. That is exactly how delete looked broken: the cache updated, the reload
      // resurrected the row. The REST paths are what the serving engine actually binds.
      const res = (isNew ? await _restCreate(saved) : await _restUpdate(m.id, saved)) as Record<string, any> | undefined;
      const sid = res?.id ?? res?.message?.id;
      if (isNew && sid) saved.id = String(sid);
      const j = (_cache ?? []).findIndex((x) => x.id === m.id);
      logUpsert("message", j >= 0 ? ((_cache ?? [])[j] as unknown as Record<string, unknown>) : null, saved as unknown as Record<string, unknown>, { name: saved.name });
      _cache = j >= 0 ? (_cache ?? []).map((x) => (x.id === m.id ? saved : x)) : [saved, ...(_cache ?? [])];
      notify();
      return saved;
    },
    duplicate: async (id: string): Promise<MessageItem | null> => {
      const s = (_cache ?? []).find((x) => x.id === id);
      if (!s) return null;
      const c: MessageItem = { ...s, id: tmpId(), name: i18nT("messaging.useMessaging.sNameCopy.icu", { value0: s.name }), status: "draft", updated: "Just now" };
      const res = await _restDuplicate(id) as Record<string, any> | undefined; // REST always — see the shim note in upsert
      const sid = res?.id ?? res?.message?.id;
      if (sid) c.id = String(sid);
      _cache = [c, ...(_cache ?? [])];
      notify();
      return c;
    },
    remove: async (id: string) => {
      await _restDelete(id); // REST always — the typed-op shim "succeeded" without a network call
      
      const prev = (_cache ?? []).find((x) => x.id === id);
      if (prev) logDelete("message", prev as unknown as Record<string, unknown>, { name: prev.name });
      _cache = (_cache ?? []).filter((x) => x.id !== id);
      notify();
    },
    reload: () => { _cache = null; _loadError = null; loadAll(); },
  };
}

/** Count of library messages, loading the cache if needed (for the rail tabs). */
export function useMessageCount(): number {
  const store = useMessaging();
  return store.all().length;
}
